/**
 * Reduce a redirect target to a path on the current origin, or null when it cannot be.
 *
 * Two distinct hazards, one fix:
 *
 * - **Absolute links we build ourselves.** `onboarding_link` is built from
 *   `config('app.url')`, so it is pinned to a single host. Following it from a language
 *   subdomain moves the browser across origins, and the session cookie is host-only
 *   (auth-proxy sets no `Domain` attribute), so the user arrives as a guest and is bounced
 *   back to a login page. Crossing subdomains is only safe via `/auth/transfer/{subdomain}`.
 * - **Targets we do not control**, such as a `previous` query parameter or an OAuth return
 *   URL. Dropping the host is what stops them sending the user off-site.
 *
 * Scheme and host are always discarded, so `https://evil.com/x` and `//evil.com/x` reduce
 * to `/x`. Opaque schemes such as `javascript:alert(1)` have no absolute path and yield
 * null rather than a stray relative navigation.
 *
 * Null is returned rather than a default so each caller keeps its own fallback: some
 * navigate to '/', others fall through to further logic.
 */
export function sameOriginPath(target: string | null | undefined): string | null {
  if (!target) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(target, window.location.origin);
  } catch {
    return null;
  }

  // Opaque schemes (javascript:, mailto:) parse but carry no absolute path.
  if (!url.pathname.startsWith('/')) {
    return null;
  }

  return url.pathname + url.search + url.hash;
}
